
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { ProductApiModel } from '@/interfaces/ProductApiModel.js'
import BaseImage from '@/components/common/BaseImage.vue'
import { useOptionStore } from '@/store/modules/options'
import { OptionApiModel } from '@/interfaces/OptionApiModel.js'

@Component({
  components: {
    BaseImage
  }
})
export default class LegacyProductRenderer extends Vue {
  public optionStore = useOptionStore();

  @Prop() readonly model!: ProductApiModel
  @Prop({ default: false }) readonly disabled!: boolean
  @Prop({ default: false }) readonly thumbnail!: boolean

  public loadCounter = 0

  get imageSize(): string {
    return this.thumbnail ? '_th.png' : '_lg.png'
  }

  get modelSize(): string {
    return (+this.model.configurator.width) > 48 ? 'WIDE' : 'CLOSE'
  }

  private baseUrl = 'configurator/'
  private noImage = 'configurator/clear.png'

  get faucetImage(): string {
    if (this.model.configurator.countertopCategory === 'NONE' || this.model.configurator.countertop === 'NONE') {
      return this.baseUrl + 'faucet/' + this.model.configurator.faucetStyle + '/' + this.model.configurator.metalFinish + '-3-' + this.model.defaultEdgeProfile + '-' + this.model.configurator.sinkOffset + '-' + this.model.configurator.doubleSink + '-' + this.modelSize + this.imageSize
    }

    return this.model.configurator.faucet === '0' || this.model.configurator.faucet === null || this.model.configurator.sink === 'NONE' || this.model.configurator.sink === null
      ? this.noImage
      : this.baseUrl + 'faucet/' + this.model.configurator.faucetStyle + '/' + this.model.configurator.metalFinish + '-' + this.model.configurator.faucet + '-' + this.correctedEdgeProfile + '-' + this.model.configurator.sinkOffset + '-' + this.model.configurator.doubleSink + '-' + this.modelSize + this.imageSize
  }

  get sinkImage(): string {
    if (this.model.configurator.countertopCategory === 'NONE' || this.model.configurator.countertop === 'NONE') {
      return this.baseUrl + 'sink/ROXY-PW-' + this.model.defaultEdgeProfile + '-' + this.model.configurator.sinkOffset + '-' + this.model.configurator.doubleSink + '-' + this.modelSize + this.imageSize
    }

    let countertop = this.model.configurator.countertop
    const split = countertop.split('-')
    if (split.length > 1) { countertop = split[1] }

    return !this.hasUndermountSink(this.model.configurator.countertop) ||
      this.model.configurator.sink === 'NONE' ||
      this.model.configurator.sink === null
      ? this.noImage
      : this.baseUrl + 'sink/' + this.model.configurator.sink + '-' + countertop + '-' + this.correctedEdgeProfile + '-' + this.model.configurator.sinkOffset + '-' + this.model.configurator.doubleSink + '-' + this.modelSize + this.imageSize
  }

  get topImage(): string {
    if (this.model.configurator.countertopCategory === 'NONE' || this.model.configurator.countertop === 'NONE') {
      return this.baseUrl + 'top/' + this.getSubfolder('countertop') + 'QT-PW-' + this.model.defaultEdgeProfile + '-' + this.model.configurator.width + '-' + 'F' + this.imageSize
    }

    let width = this.model.configurator.width.toString()
    if (this.model.configurator.countertopCategory === 'MS' && (width === '60' || width === '72') && (this.model.configurator.sink === 'MSR1' || this.model.configurator.sink === 'MSR2')) {
      width = width + 'D'
    }

    let topImageUrl = this.baseUrl + 'top/' + this.getSubfolder('countertop') + this.model.configurator.countertop + '-' + this.model.configurator.edgeProfile + '-' + width + '-' + this.model.configurator.overhang

    if (this.addSinkNameToRender(this.model.configurator.countertop)) {
      topImageUrl += '-' + this.model.configurator.sink
    }

    return topImageUrl + this.imageSize
  }

  get panelsImage(): string {
    return !this.hasRenders('panelFinish')
      ? ''
      : this.baseUrl + this.model.code + '/' + this.model.configurator.configuration + this.model.configurator.width + '/panels/' + this.model.configurator.panelFinish + this.imageSize
  }

  get metalImage(): string {
    return this.model.hasMetalFrame ? this.baseUrl + this.model.code + '/' + this.model.configurator.configuration + this.model.configurator.width + '/metal/' + this.model.configurator.metalFinish + this.imageSize : this.noImage
  }

  public hardwareImage(frame?: number): string {
    return this.model.configurator.hardware === 'NONE' || this.model.configurator.hardware === null || !this.hasRenders('hardware') ? this.noImage : this.baseUrl + this.model.code + '/' + this.model.configurator.configuration + this.model.configurator.width + '/hardware/' + this.model.configurator.hardware + '-' + this.model.configurator.metalFinish + (frame !== undefined ? '-0' + frame : '') + this.imageSize
  }

  public accentImage(frame?: number): string {
    return this.model.configurator.accent === 'NONE' || this.model.configurator.accent === null || this.model.configurator.accent === undefined ? this.noImage : this.baseUrl + this.model.code + '/' + this.model.configurator.configuration + this.model.configurator.width + '/hardware/' + this.model.configurator.accent + '-' + this.model.configurator.metalFinish + (frame !== undefined ? '-0' + frame : '') + this.imageSize
  }

  get backImage(): string {
    return this.baseUrl + this.model.code + '/' + this.model.configurator.configuration + this.model.configurator.width + '/background/b1' + this.imageSize
  }

  get metalBaseImage(): string {
    return !this.isMetalBaseSet ? this.noImage : this.baseUrl + 'mb/' + this.correctedBaseCode + '-' + this.model.configurator.metalFinish + this.imageSize
  }

  get correctedBaseCode(): string {
    if (this.isBaseSet) {
      return this.baseCode + '-' + this.model.configurator.width + this.baseHeight
    }

    return ''
  }

  get metalBaseShelfImage(): string {
    return (!this.isMetalBaseSet || !this.model.configurator.base.startsWith('MB4')) ? this.noImage : this.baseUrl + 'mb/' + this.correctedMetalBaseShelfCode + '-' + this.basePanelFinish + this.imageSize
  }

  get correctedMetalBaseShelfCode(): string {
    if (this.isMetalBaseSet) {
      return this.baseCode + '-SHELF-' + this.model.configurator.width + this.baseHeight
    }

    return ''
  }

  get basePanelFinish() {
    let panelFinish = this.model.configurator.panelFinish
    if (panelFinish.endsWith('F')) {
      panelFinish = panelFinish.substring(0, panelFinish.length - 1)
    }

    return panelFinish
  }

  get woodBaseImage(): string {
    return !this.isWoodBaseSet
      ? this.noImage
      : this.baseUrl + 'option/' + this.baseCode + '/' + this.correctedBaseCode + '-' + this.basePanelFinish + this.imageSize
  }

  get isWoodBaseSet() {
    return this.isBaseSet &&
      this.model.configurator.base.startsWith('WB')
  }

  get isMetalBaseSet() {
    return this.isBaseSet &&
      (this.model.configurator.base.startsWith('MB') ||
        this.model.configurator.base.startsWith('DAWSON') ||
        this.model.configurator.base.startsWith('DEAN'))
  }

  get isBaseSet() {
    return this.model.configurator.base !== undefined &&
      this.model.configurator.base !== null &&
      this.model.configurator.base !== 'NONE'
  }

  get hardwareFrames(): number {
    if (this.model.configurator.hardwareFrames === undefined || isNaN(+(this.model.configurator.hardwareFrames))) return 0
    return +(this.model.configurator.hardwareFrames)
  }

  get accentFrames(): number {
    if (this.model.configurator.accentFrames === undefined || isNaN(+(this.model.configurator.accentFrames))) return 0
    return +(this.model.configurator.accentFrames)
  }

  get correctedEdgeProfile(): string {
    return this.model.configurator.edgeProfile.replace('OG', '.0')
  }

  get backLegImage(): string {
    if (this.model.hasLegs) {
      return this.baseUrl + this.model.code + '/LEGS/' + this.model.configurator.width + '/background/b1' + this.imageSize
    }
    return this.noImage
  }

  get panelsLegImage(): string {
    if (this.model.hasLegs) {
      return this.baseUrl + this.model.code + '/LEGS/' + this.model.configurator.width + '/panels/' + this.model.configurator.panelFinish + this.imageSize
    }
    return this.noImage
  }

  get legHardwareImage(): string {
    if (this.model.hasLegs && this.model.configurator.legHardware !== 'NONE' && this.model.configurator.legHardware !== null) {
      return this.baseUrl + this.model.code + '/LEGS/' + this.model.configurator.width + '/metal/' + this.model.configurator.metalFinish + this.imageSize
    }
    if (this.model.hasConfigurationLegs) {
      return this.baseUrl + this.model.code + '/' + this.model.configurator.configuration + this.model.configurator.width + '/legs/' + this.model.configurator.metalFinish + this.imageSize
    }
    return this.noImage
  }

  get baseCode(): string {
    if (this.isBaseSet) {
      const index = this.model.configurator.base.split('-')
      return index[0]
    }

    return ''
  }

  get baseHeight(): string {
    if (this.isBaseSet) {
      const index = this.model.configurator.base.split('-')
      return index[1]
    }

    return ''
  }

  public imageLoading() {
    this.loadCounter++
  }

  public imageLoaded() {
    this.loadCounter--
  }

  @Watch('model', { immediate: true, deep: true })
  onModelChanged(val: ProductApiModel, oldVal: ProductApiModel) {
    if (JSON.stringify(val) !== JSON.stringify(oldVal)) {
      this.loadCounter = 0
    }
  }

  public hasRenders(modelName: string) {
    const option = this.getOption(modelName)
    return option?.hasRenders ?? true
  }

  public getSubfolder(modelName: string) {
    const option = this.getOption(modelName)
    const productOption = option?.products.find(p => p.productCode === this.model.code)
    console.debug('ProductRenderer: Subfolder for ' + this.model.code, productOption?.subfolder)
    if (productOption?.subfolder) {
      return productOption.subfolder + '/'
    }

    return ''
  }

  public hasUndermountSink(optionCode: string) {
    const option = this.optionStore.allOptions?.find(o => o.code === optionCode)
    return option?.hasUndermountSink ?? true
  }

  public addSinkNameToRender(optionCode: string) {
    const option = this.optionStore.allOptions?.find(o => o.code === optionCode)
    return option?.addSinkNameToRender ?? true
  }

  private getOption(modelName: string) {
    const selectedValue = this.model.configurator[modelName]
    const selectedCategory = this.model.configurator[modelName + 'Category']
    let options: Array<OptionApiModel> | undefined = []
    if (selectedCategory) {
      options = this.optionStore.allCategories.find(c => c.code === selectedCategory)?.options
    } else {
      options = this.optionStore.allOptions
    }

    const option = options?.find(o => o.code === selectedValue)
    return option
  }
}
